import React, { createContext, useState, useContext, useEffect } from "react";
import authApi from "../api/authApi";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [roles, setRoles] = useState([]); // Array: ['seller', 'rider']
  const [activeRole, setActiveRole] = useState(null); // 'seller' | 'rider'
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let token = localStorage.getItem("byka_token");
    if (!token) {
      const legacyToken =
        localStorage.getItem("daas_token") ||
        localStorage.getItem("seller_token") ||
        localStorage.getItem("rider_token");
      if (legacyToken) {
        token = legacyToken;
        localStorage.setItem("byka_token", legacyToken);
        localStorage.removeItem("daas_token");
        localStorage.removeItem("seller_token");
        localStorage.removeItem("rider_token");
      }
    }

    if (token) {
      authApi
        .get("/auth/me")
        .then((res) => {
          setUser(res.data.user);
          setRoles(res.data.roles || []);

          let currentTarget =
            localStorage.getItem("byka_active_role") ||
            localStorage.getItem("daas_active_role");
          if (!currentTarget || !res.data.roles.includes(currentTarget)) {
            currentTarget = res.data.roles.includes("seller")
              ? "seller"
              : "rider";
          }
          setActiveRole(currentTarget);
          localStorage.setItem("byka_active_role", currentTarget);
          localStorage.removeItem("daas_active_role");
        })
        .catch(() => {
          localStorage.removeItem("byka_token");
          localStorage.removeItem("byka_active_role");
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (phone, password) => {
    const res = await authApi.post("/auth/login", { phone, password });

    localStorage.setItem("byka_token", res.data.token);
    localStorage.setItem("byka_active_role", res.data.activeRole);

    setUser(res.data.user);
    setRoles(res.data.roles || []);
    setActiveRole(res.data.activeRole);

    return res.data;
  };

  const register = async (data) => {
    const res = await authApi.post("/auth/register", data);

    localStorage.setItem("byka_token", res.data.token);
    localStorage.setItem("byka_active_role", res.data.activeRole);

    // Refresh strictly to get full nested profiles
    const meRes = await authApi.get("/auth/me");
    setUser(meRes.data.user);
    setRoles(meRes.data.roles || []);
    setActiveRole(res.data.activeRole);

    return res.data;
  };

  const switchRole = (targetRole) => {
    if (roles.includes(targetRole)) {
      setActiveRole(targetRole);
      localStorage.setItem("byka_active_role", targetRole);
      window.location.href = targetRole === "rider" ? "/rider" : "/seller"; // Hard refresh prevents stale data
    }
  };

  const logout = () => {
    localStorage.removeItem("byka_token");
    localStorage.removeItem("byka_active_role");
    setUser(null);
    setRoles([]);
    setActiveRole(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        roles,
        activeRole,
        loading,
        login,
        register,
        logout,
        switchRole,
        setUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
